import { Divider, Required, WizardFooter } from "@components"
import { Chip, Select, SelectItem, Slider, Switch } from "@heroui/react"
import { Controller, useForm } from "react-hook-form";
import { PropTypes } from "prop-types"

const EventSettings = ({
  includeSecondaryContacts = false,
  vcAutoDisconnect = false, 
  maxBookingsPerStudent = 0, 
  showWizardFooter = true, 
  venueId = null, 
  isVcEvent, 
  isVenueEvent, 
  handlePrevious = () => {}, 
  handleSubmit, 
  venues = []
}) => {
  const { control, handleSubmit: handleValidateForm, formState: { errors }, getValues, watch } = useForm({
    defaultValues: {
      include_secondary_contacts: includeSecondaryContacts,
      vc_auto_disconnect: vcAutoDisconnect,
      max_bookings_per_student: maxBookingsPerStudent,
      venue_id: venueId,
    }
  })

  const maxBookings = watch("max_bookings_per_student");

  return (
    <form id="event-settings-form" className="flex flex-col gap-y-4" noValidate>
      <div>
        <h5 className="mb-1 text-base font-semibold text-foreground">Who to invite</h5>
        <p className="mb-3 text-sm text-content2-foreground">Select primary contacts only or include secondary contacts (This is MIS dependant).</p>

        <Controller
          control={control}
          name="include_secondary_contacts"
          render={({ field: { value, onChange } }) => (
            <Switch defaultSelected={value} onChange={onChange}>Include Secondary Contacts</Switch>
          )}
        />

        <Divider className="mt-4" />
      </div>

      {isVcEvent && (
        <div>
          <h5 className="mb-1 text-base font-semibold text-foreground">Video Call Options</h5>
          <p className="mb-3 text-sm text-content2-foreground">Automatically disconnect calls once slot time has expired.</p>

          <Controller
            control={control}
            name="vc_auto_disconnect"
            render={({ field: { value, onChange } }) => (
              <Switch defaultSelected={value} onChange={onChange}>Auto Disconnect</Switch>
            )}
          />

          <Divider className="mt-4" />
        </div>
      )}
      

      {isVenueEvent && (
        <div>
          <h5 className="mb-1 text-base font-semibold text-foreground">Venue Options<Required /></h5>
          <p id="venue-label" className="mb-0 text-sm text-content2-foreground">Select where the event will take place.</p>

          <Controller
            control={control}
            rules={{ required: "You must supply a venue for your event" }}
            name="venue_id"
            render={({ field: { value, onChange } }) => (
              <Select
                classNames={{ "label": "mt-2", "mainWrapper": "mt-3" }}
                isRequired
                placeholder="Select a venue"
                disallowEmptySelection={true}
                onChange={onChange}
                selectionMode="single"
                defaultSelectedKeys={[value]}
                isInvalid={errors.hasOwnProperty("venue_id")}
                aria-labelledby="venue-label"
              >
                {venues.map((venue) => (
                  <SelectItem key={venue.id} value={venue.id}>
                    {venue.name}
                  </SelectItem>
                ))}
              </Select>
            )}
          />

          <Divider className="mt-4" />
        </div>
      )}

      <div>
        <h5 className="mb-1 text-base font-semibold text-foreground">Other Options</h5>
        <p className="mb-3 text-sm text-content2-foreground">
          Set the maximum number of bookings a guardian can make per student for this event. If 0 then no limit is set.
        </p>

        <div className="flex items-center justify-between">
          <p className="text-sm">Max Bookings Per Student</p>
          
          <Chip color="success" className="text-white">
            <span className="font-semibold">{maxBookings === 0 ? "Unlimited" : `${maxBookings} Bookings`}</span>
          </Chip>
        </div>  

        <Controller
          control={control}
          name="max_bookings_per_student"
          render={({ field: { value, onChange } }) => (
            <Slider
              classNames={{ thumb: "shadow-none bg-blue-600 after:bg-blue-600 after:shadow-none h-6 w-6 border-none" }}
              defaultValue={value}
              size="md"
              step={1}
              maxValue={100}
              minValue={0}
              onChange={onChange}
            />
          )}
        />
      </div>

      {showWizardFooter && (
        <WizardFooter currentStep={1} totalSteps={3} previousCaption="Meeting Types" nextCaption="Sessions" handlePrevious={() => handlePrevious(getValues())} handleNext={handleValidateForm((data) => handleSubmit(data))} />
      )}
    </form>
  )
}

EventSettings.propTypes = {
  includeSecondaryContacts: PropTypes.bool,
  vcAutoDisconnect: PropTypes.bool,
  isVcEvent: PropTypes.bool.isRequired,
  isVenueEvent: PropTypes.bool.isRequired,
  handlePrevious: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  maxBookingsPerStudent: PropTypes.number,
  showWizardFooter: PropTypes.bool,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  venues: PropTypes.array
}

export default EventSettings